import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Box, Icon } from '@openfin/ui-library';
import styled from 'styled-components';

import { PanelButton } from './PanelButton';

import MagnifierIcon from '../../../../../res/provider/ui/image/shapes/magnifier.svg';
import { WindowContext } from '../../contexts/WindowContext';
import { HistoryContext } from '../../contexts/HistoryContext';
import { NotificationView } from '../../components/NotificationView/NotificationView';
import { Header } from '../../components/Header/Header';
import { RootState } from '../../../store/State';
import { GroupingType } from '../../utils/Grouping';
import { StoredNotification } from '../../../model/StoredNotification';
import { StoredApplication } from '../../../model/Environment';
import { ROUTES } from '../../routes';
import {
  RemoveNotifications,
  ToggleCenterVisibility,
  ToggleCenterVisibilitySource,
} from '../../../store/Actions';
import { SortingPanel } from '../../components/SortingPanel/SortingPanel';
import { TextInput } from '../../components/TextInput/TextInput';
import { ClearSearch, SearchNotifications, SetSearchMode } from '../../../store/partials/Search';
import { StoredStream } from '../../../model/StoredStream';
import { GlobalProviderStyles } from '../../styles/GlobalProviderStyles';

interface Props extends ReturnType<typeof mapDispatch> {
  notifications: StoredNotification[];
  applications: ReadonlyMap<string, StoredApplication>;
  streams: ReadonlyMap<string, StoredStream>;
  centerLocked: boolean;
  searchMode: boolean;
  searchQuery: string;
  visible: boolean;
}

const NotificationsPanelComponent = ({
  notifications,
  applications,
  streams,
  searchMode,
  searchQuery,
  visible,
  clearSearch,
  removeNotifications,
  setSearchMode,
  searchNotifications,
  closeCenter,
}: Props): JSX.Element | null => {
  const history = React.useContext(HistoryContext);
  const window = React.useContext(WindowContext);
  const [groupBy, setGroupBy] = React.useState(GroupingType.DATE);

  React.useEffect(() => {
    window.document.title = 'Center';
  }, []);

  const handleClearAll = () => {
    removeNotifications(notifications);
  };

  const handleSearch = (event: { name: string; value: string }) => {
    if (event.value.length > 0) {
      searchNotifications(event.value.trim());
    } else {
      clearSearch();
    }
  };

  if (!visible) {
    return null;
  }

  const handleBackButtonClick = searchMode ? () => setSearchMode(false) : undefined;

  const handleSearchButtonClick = () => {
    setSearchMode(!searchMode);
  };

  const handleSettingButtonClick = () => {
    if (searchMode) {
      setSearchMode(false);
    }
    history.push(ROUTES.SETTINGS);
  };

  return (
    <Container flexDirection="column" flexGrow={1}>
      <GlobalProviderStyles />
      <Header
        title="Notifications"
        onBack={handleBackButtonClick}
        onClose={closeCenter}
        badgeCount={notifications.length}
      >
        <PanelButton aria-label="Search" onClick={handleSearchButtonClick}>
          <Icon icon="MagnifyingGlassIcon" />
        </PanelButton>
        <PanelButton aria-label="Settings" onClick={handleSettingButtonClick}>
          <Icon icon="GearIcon" />
        </PanelButton>
      </Header>
      {searchMode ? (
        <SearchContainer>
          <TextInput
            onChange={handleSearch}
            clearControl
            focusOnMount
            icon={MagnifierIcon}
            placeholder="Search within your Notifications"
          />
        </SearchContainer>
      ) : (
        <SortingPanel
          groupBy={groupBy}
          onSortingChange={(value) => setGroupBy(value as GroupingType)}
          onClearAll={handleClearAll}
        />
      )}
      <NotificationView
        id="notification-panel-view"
        notifications={notifications}
        searchMode={searchMode}
        searchString={searchQuery}
        applications={applications}
        streams={streams}
        groupBy={groupBy}
      />
    </Container>
  );
};

const Container = styled(Box)`
  background: ${({ theme }) => theme.palette.background2};
  height: 100%;

  &:after {
    position: absolute;
    content: '';
    pointer-events: none;
    top: 1px;
    left: 1px;
    right: 1px;
    bottom: 1px;
    box-shadow: 0 0 1px 1px ${({ theme }) => theme.palette.background4};
    border-radius: ${({ theme }) => theme.radius.base};
  }
`;

const SearchContainer = styled.div`
  margin: ${({ theme }) => theme.px.large} auto;
`;

const mapStateToProps = (state: RootState) => ({
  notifications: state.notifications,
  applications: state.applications,
  streams: state.streams,
  centerLocked: state.centerLocked,
  searchResults: state.search.results, // TODO: use actual results
  searchQuery: state.search.query,
  searchMode: state.search.searchMode,
  visible: state.centerVisible,
});

const mapDispatch = (dispatch: Dispatch) => ({
  setSearchMode: (mode: boolean) => dispatch(new SetSearchMode(mode)),
  searchNotifications: (text: string) => dispatch(new SearchNotifications(text)),
  clearSearch: () => dispatch(new ClearSearch()),
  removeNotifications: (notifications: StoredNotification[]) =>
    dispatch(new RemoveNotifications(notifications)),
  closeCenter: () =>
    dispatch(new ToggleCenterVisibility(ToggleCenterVisibilitySource.BUTTON, false)),
});

export const NotificationsPanel = connect(
  mapStateToProps,
  mapDispatch
)(NotificationsPanelComponent);
